import { Box, useMediaQuery } from '@mui/material';
import { createFileRoute, Outlet, redirect, useLocation } from '@tanstack/react-router';
import { RoutePaths } from 'common-types';
import { BottomTabBar } from 'components/bottom-tab-bar';
import { HeaderDesktop } from 'components/header-desktop';
import { TrustpilotFeedbackModal } from 'components/modals/trustpilot-feedback';
import { ProfileDialog } from 'components/profile-dialog';
import { RouteErrorBoundary } from 'components/route-errors';
import { QueryParam, useParseParamFromQuery } from 'hooks/general/use-parse-param-from-query';
import theme from 'providers/theme/theme';
import { useCallback } from 'react';
import chicApi from 'services/chic-api';
import { useLocalStorageStore, useStore } from 'store';
import { useShallow } from 'zustand/shallow';

const pagesWithGreyBg = [RoutePaths.SUBSCRIPTION_INFO, RoutePaths.UNSUBSCRIBE];

export const Route = createFileRoute('/_authenticated/_main')({
  beforeLoad: async () => {
    const { hasSeenOnboarding, setHasSeenOnboarding } = useLocalStorageStore.getState();

    if (hasSeenOnboarding) return;

    const completedSteps = await chicApi.getCompletedSteps();
    const steps = Array.isArray(completedSteps) ? completedSteps : [];

    if (steps.length > 0) {
      setHasSeenOnboarding(true);
      return;
    }

    // @ts-expect-error strictNullChecks breaks TanStack Router inference
    throw redirect({ to: '/onboarding/my-plan/$step', params: { step: '1' } });
  },
  component: MainLayout,
  errorComponent: RouteErrorBoundary,
});

function MainLayout() {
  const isMediumAndDown = useMediaQuery(theme.breakpoints.down('lg'));
  const location = useLocation();
  const pathname = location.pathname.replace(/\/$/, '') || '/';
  const source = useParseParamFromQuery(QueryParam.SOURCE);

  const isArticlePage = pathname === '/article';
  const isOnboardingArticle = isArticlePage && source === 'onboarding';

  const { isReviewModalOpen, isInfoScreenOpen, reviewModalPlacement, reviewModalDay } = useStore(
    useShallow((state) => ({
      isReviewModalOpen: state.isReviewModalOpen,
      isInfoScreenOpen: state.isInfoScreenOpen,
      reviewModalPlacement: state.reviewModalPlacement,
      reviewModalDay: state.reviewModalDay,
    })),
  );

  const { isReviewModalShown } = useLocalStorageStore(
    useShallow((state) => ({
      isReviewModalShown: state.isReviewModalShown,
    })),
  );

  const handleReviewClose = useCallback((): void => {
    useStore.getState().setIsReviewModalOpen(false);
    useLocalStorageStore.getState().setIsReviewModalShown(true);
  }, []);

  const shouldHideNavigation = isInfoScreenOpen || isOnboardingArticle;

  return (
    <Box
      component="main"
      display="flex"
      flexDirection="column"
      pb={shouldHideNavigation ? 0 : { xs: 25, lg: 0 }}
      pt={shouldHideNavigation ? 0 : { xs: 0, lg: 30 }}
      bgcolor={pagesWithGreyBg.includes(pathname as RoutePaths) ? '#F4F4F4' : 'common.white'}
    >
      {!shouldHideNavigation && (isMediumAndDown ? <BottomTabBar /> : <HeaderDesktop />)}
      <Box
        minHeight="100vh"
        width={1}
        maxWidth={{ lg: 1280 }}
        mx={{ lg: 'auto' }}
        bgcolor={pagesWithGreyBg.includes(pathname as RoutePaths) ? '#F4F4F4' : 'common.white'}
        px={shouldHideNavigation ? 0 : { xs: 0, lg: 6 }}
        py={shouldHideNavigation ? 0 : { lg: 8 }}
      >
        <Outlet />
      </Box>
      <ProfileDialog />
      <TrustpilotFeedbackModal
        isOpen={isReviewModalOpen && !isReviewModalShown}
        onClose={handleReviewClose}
        placement={reviewModalPlacement}
        day={reviewModalDay}
      />
    </Box>
  );
}
